import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import LeftNav from "../../components/common/LeftNav";
import { useResponsive } from "../../hooks/useResponsive";
import { useThemeMode } from "../../hooks/useThemeMode";
import { useConversation } from "../../hooks/useConversation";
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { motion } from 'framer-motion';
import eventIcon from "../../assets/icons/Calendar_Days.svg";

// Import the view components we already created
import ListView from "../../components/layout/EventsList";
import CalendarView from "../../components/layout/EventsCalendar";
import ViewToggle from "../../components/specific/EventsViewToggle";
import {useNav} from "../../context/NavContext";

/**
 * Events page component that displays campus events in different views
 * @returns {JSX.Element} The Events page component
 */
const EventsPage = () => {
  const MAX_CONTEXT_TOKENS = 4000;
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnPhone, forceClose } = useResponsive();
  const { isLightMode } = useThemeMode();
  const {
    handleNewConversation,
    handleSearchWithText
  } = useConversation(MAX_CONTEXT_TOKENS);
  const { navExpanded, toggleNavExpanded } = useNav();


  const [allEvents, setAllEvents] = useState([]);
  const [eventsByDate, setEventsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [activeDate, setActiveDate] = useState(null);

  // View state - default to list view
  const [activeView, setActiveView] = useState('list');

  // Improved scroll state tracking with additional stabilization
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const isScrollingRef = useRef(false); // Ref to track scrolling state outside of render cycle
  const scrollTimeoutRef = useRef(null);
  const lastScrollActionTime = useRef(Date.now());
  const scrollStabilityDelay = 150; // ms to wait for scroll to stabilize

  // Refs for scrolling functionality
  const timelineRefs = useRef({});
  const contentRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const timelineContainerRef = useRef(null);
  const activeIndicatorRef = useRef(null);

  // Track the last programmatic scroll to prevent feedback loops
  const lastProgrammaticScrollTarget = useRef(null);

  // Combined refs object to pass to child components
  const scrollRefs = {
    timelineRefs,
    contentRefs,
    scrollContainerRef,
    timelineContainerRef,
    activeIndicatorRef
  };

  // Extract view type from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewParam = params.get('view');
    if (viewParam === 'calendar' || viewParam === 'list') {
      setActiveView(viewParam);
    }
  }, [location]);

  // Handle view change
  const handleViewChange = useCallback((view) => {
    if (view === 'list' || view === 'calendar') {
      setActiveView(view);
      navigate(`/events?view=${view}`, { replace: true });
    }
  }, [navigate]);

  // Handle navigation to networking page
  const handleNetworkingClick = () => {
    navigate('/networking');
  };

  // Redirect to log in if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
    }
  }, [currentUser, navigate]);

  // Get today's date in YYYY-MM-DD format
  const getTodayString = useCallback(() => {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const adjustedToday = new Date(today.getTime() - (offset * 60 * 1000));
    return adjustedToday.toISOString().split('T')[0];
  }, []);

  // Check if a date is today
  const isToday = useCallback((dateString) => {
    return dateString === getTodayString();
  }, [getTodayString]);

  // Fetch events data from Firestore
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const eventsRef = collection(db, "currentEvents");
        const eventsSnapshot = await getDocs(eventsRef);
        const eventData = {};
        const dates = [];
        const allEventsArray = [];

        eventsSnapshot.forEach((doc) => {
          const dateStr = doc.id;
          const data = doc.data();
          if (!data.events || !Array.isArray(data.events) || data.events.length === 0) return;
          dates.push(dateStr);
          const processedEvents = data.events.map(event => ({
            ...event,
            date: dateStr,
            displayTime: event.displayTime || (event.dateTime ?
              new Date(event.dateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) :
              "All day")
          }));
          eventData[dateStr] = processedEvents;
          allEventsArray.push(...processedEvents);
        });

        const sortedDates = [...dates].sort((a, b) => new Date(a) - new Date(b));
        setEventsByDate(eventData);
        setAvailableDates(sortedDates);
        setAllEvents(allEventsArray);

        // When setting the initial active date
        const today = getTodayString();
        let initialActiveDate = null;

        // First priority: Today
        if (sortedDates.includes(today)) {
          initialActiveDate = today;
        }
        // Second priority: Find closest date to today
        else {
          const closestDate = sortedDates.reduce((closest, date) => {
            const currentDiff = Math.abs(new Date(date) - new Date(today));
            const closestDiff = Math.abs(new Date(closest) - new Date(today));
            return currentDiff < closestDiff ? date : closest;
          }, sortedDates[0]);

          initialActiveDate = closestDate;
        }

        setActiveDate(initialActiveDate);
        lastProgrammaticScrollTarget.current = initialActiveDate;

        setLoading(false);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError("Failed to load events. Please try again later.");
        setLoading(false);
      }
    };

    fetchEvents();
  }, [getTodayString]);

  // Setup refs for each date section after data is loaded
  useEffect(() => {
    if (availableDates.length > 0) {
      timelineRefs.current = {};
      contentRefs.current = {};
      availableDates.forEach(date => {
        timelineRefs.current[date] = React.createRef();
        contentRefs.current[date] = React.createRef();
      });
    }
  }, [availableDates]);

  // Find the date section currently most visible in the viewport
  const findMostVisibleDateSection = useCallback(() => {
    if (!scrollContainerRef.current || availableDates.length === 0) return null;

    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const containerTop = containerRect.top;
    const containerHeight = containerRect.height;
    const scrollThreshold = containerTop + 60; // Priority to sections near the top

    // First priority: section whose top is closest to the threshold
    let closestToThreshold = null;
    let smallestThresholdDistance = Infinity;

    // Second priority: section with highest visibility percentage
    let mostVisibleDate = null;
    let highestVisibility = 0;

    availableDates.forEach(date => {
      const contentElement = contentRefs.current[date]?.current;
      if (!contentElement) return;

      const rect = contentElement.getBoundingClientRect();

      // If top is visible and close to threshold
      if (rect.top <= scrollThreshold && rect.bottom > containerTop) {
        const distanceToThreshold = Math.abs(rect.top - scrollThreshold);
        if (distanceToThreshold < smallestThresholdDistance) {
          smallestThresholdDistance = distanceToThreshold;
          closestToThreshold = date;
        }
      }

      // Calculate visibility percentage as fallback
      const visibleTop = Math.max(rect.top, containerTop);
      const visibleBottom = Math.min(rect.bottom, containerTop + containerHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibilityPercentage = visibleHeight / rect.height;

      if (visibilityPercentage > highestVisibility) {
        highestVisibility = visibilityPercentage;
        mostVisibleDate = date;
      }
    });

    // Prefer threshold-based detection, fall back to visibility percentage
    return closestToThreshold || mostVisibleDate;
  }, [availableDates]);

  // Scroll the timeline to center the active date button
  const scrollTimelineToActiveDate = useCallback((date, behavior = 'smooth') => {
    const timelineElement = timelineRefs.current[date]?.current;
    if (!timelineElement || !timelineContainerRef.current) return;

    const containerHeight = timelineContainerRef.current.offsetHeight;
    const elementTop = timelineElement.offsetTop;
    const elementHeight = timelineElement.offsetHeight;
    const scrollTo = Math.max(0, elementTop - (containerHeight / 2) + (elementHeight / 2));

    timelineContainerRef.current.scrollTo({
      top: scrollTo,
      behavior: behavior
    });
  }, []);

  // Scroll the content area to the specified date section
  const scrollToDateContent = useCallback((date, behavior = 'smooth') => {
    if (date === lastProgrammaticScrollTarget.current) return; // Prevent duplicates

    const contentElement = contentRefs.current[date]?.current;
    if (!contentElement || !scrollContainerRef.current) return;

    lastProgrammaticScrollTarget.current = date;
    lastScrollActionTime.current = Date.now();
    isScrollingRef.current = true;

    const headerOffset = 16; // Adjust based on your header height
    scrollContainerRef.current.scrollTo({
      top: contentElement.offsetTop - headerOffset,
      behavior: behavior
    });

    // Reset scrolling flag after animation likely completes
    setTimeout(() => {
      isScrollingRef.current = false;
    }, behavior === 'smooth' ? 800 : 100);
  }, []);

  // Enhanced scroll handler with debouncing and better stabilization
  useEffect(() => {
    // Only add scroll handlers in list view
    if (activeView !== 'list') return;

    const handleContentScroll = () => {
      // Skip processing if we're in a user-initiated or programmatic scroll
      if (isScrollingRef.current || Date.now() - lastScrollActionTime.current < scrollStabilityDelay) {
        return;
      }

      // Find which date section is most visible
      const visibleDate = findMostVisibleDateSection();

      // Only update if we found a visible section and it's different from current active
      if (visibleDate && visibleDate !== activeDate) {
        // Update active date without triggering another scroll
        setActiveDate(visibleDate);

        // Scroll timeline to match, but only if we're not in a recent programmatic scroll
        if (Date.now() - lastScrollActionTime.current > 800) {
          scrollTimelineToActiveDate(visibleDate);
        }
      }
    };

    // Debounced scroll handler to reduce processing frequency
    const debouncedScrollHandler = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        handleContentScroll();
        scrollTimeoutRef.current = null;
      }, 100);
    };

    // Add scroll listener to content container
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', debouncedScrollHandler, { passive: true });
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', debouncedScrollHandler);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };

  }, [activeDate, availableDates, findMostVisibleDateSection, scrollTimelineToActiveDate, activeView]);

  // Handle click on a timeline date button
  const handleTimelineClick = useCallback((date) => {
    // Don't do anything if already active
    if (date === activeDate) return;

    // Prevent scroll handler from processing during this action
    isScrollingRef.current = true;
    setIsUserScrolling(true);

    // Update active date immediately for visual feedback
    setActiveDate(date);

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Scroll the main content to the selected date
    scrollToDateContent(date);

    // Reset user scrolling flag after animation completes
    scrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
      isScrollingRef.current = false;
    }, 800);
  }, [activeDate, scrollToDateContent]);

  // Format date for display
  const formatDisplayDate = useCallback((dateString) => {
    if (!dateString) return '';
    try {
      const date = dateString.toDate ? dateString.toDate() : new Date(`${dateString}T00:00:00`);
      if (isNaN(date.getTime())) return dateString;
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Date formatting error:", error);
      return dateString;
    }
  }, []);

  // Format date for timeline display
  const formatTimelineDate = useCallback((dateString) => {
    if (!dateString) return { day: '', month: '', weekday: '' };
    try {
      const date = dateString.toDate ? dateString.toDate() : new Date(`${dateString}T00:00:00`);
      if (isNaN(date.getTime())) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const tempDate = new Date(dateString + 'T00:00:00');
          return {
            day: parts[2],
            month: new Date(tempDate).toLocaleDateString(undefined, { month: 'short' }),
            weekday: new Date(tempDate).toLocaleDateString(undefined, { weekday: 'short' })
          };
        }
        return { day: dateString, month: '', weekday: '' };
      }
      return {
        day: date.getDate().toString(),
        month: date.toLocaleString('default', { month: 'short' }),
        weekday: date.toLocaleString('default', { weekday: 'short' })
      };
    } catch (error) {
      console.error("Date formatting error:", error);
      return { day: dateString, month: '', weekday: '' };
    }
  }, []);


  return (
      <div className="flex-1 flex flex-col min-h-0 h-full rounded overflow-clip relative transition-colors duration-300 pb-2">

        {/* Content area with views */}
        <div className="flex-1 flex transition-colors duration-300 rounded overflow-hidden">
          {loading ? (
            <div className="flex-1 flex justify-center items-center">
              <motion.div
                className="rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            </div>
          ) : error ? (
            <div className="flex-1 p-4">
              <motion.div
                className="bg-red-100 dark:bg-red-900 p-4 rounded-lg text-red-800 dark:text-red-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {error}
              </motion.div>
            </div>
          ) : availableDates.length === 0 ? (
            <div className={`flex-1 flex justify-center items-center ${theme.text.muted}`}>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                No events scheduled.
              </motion.p>
            </div>
          ) : (
            <>
                <ListView
                  availableDates={availableDates}
                  eventsByDate={eventsByDate}
                  loading={loading}
                  error={error}
                  activeDate={activeDate}
                  setActiveDate={setActiveDate}
                  formatDisplayDate={formatDisplayDate}
                  formatTimelineDate={formatTimelineDate}
                  isToday={isToday}
                  scrollRefs={scrollRefs}
                  handleTimelineClick={handleTimelineClick}
                />
            </>
          )}
        </div>
      </div>
  );
};

export default EventsPage;